import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useRealtimeCall, STATES, TEACH_API_ADAPTER,
} from '../../../hooks/useRealtimeCall';
import { formatCost } from '../../../utils/voicePricing';
import { MODEL_BY_ID } from '../../../lib/aiModels';

// «کلاس آموزش صوتی» — the admin-side voice classroom.
//
// Reuses useRealtimeCall in teach mode. Live changes (create / update /
// archive note) stream into the local `changes` list — on Gemini they come
// as server-confirmed `class_change` frames (with note_id); on OpenAI they
// come as in-flight function-call args from the data channel. Both are
// shown in the «تابلوی کلاس» panel so admin sees the class taking shape.

const ACTION_LABEL = {
  create_note:  'ثبت نتِ جدید',
  update_note:  'اصلاح نت',
  archive_note: 'بایگانیِ نت',
  search_knowledge: 'جستجو در پایگاه',
  // server-confirmed mirror (geminiProvider sends `change.action` as these):
  created:  'ثبت شد',
  updated:  'اصلاح شد',
  archived: 'بایگانی شد',
};

// The cost line names the model that answered (its catalogue label).
function modelLabel(model) {
  return (model && MODEL_BY_ID[model]?.label) || model || '';
}

export default function TeachVoicePanel() {
  const [changes, setChanges] = useState([]);

  // Add a change row whenever the agent does anything visible. We dedup
  // in-flight rows (OpenAI's pre-save signals) against the server-confirmed
  // mirror that arrives shortly after — same args + matching action prefix.
  const onChange = useCallback((change) => {
    setChanges((prev) => [
      ...prev,
      { ...change, at: Date.now(), id: `c-${prev.length}-${Date.now()}` },
    ]);
  }, []);

  const call = useRealtimeCall(null, {
    mode:    'teach',
    api:     TEACH_API_ADAPTER,
    onChange,
  });

  // Resync on mount in case a previous tab/process left a class active.
  useEffect(() => { /* status sync left to a future polish — not load-bearing */ }, []);

  // Reset the changes list when a new class starts (state goes from idle
  // → mic → connecting). The previous class's log is preserved nowhere
  // permanent in this MVP; admin should re-open the panel after end.
  useEffect(() => {
    if (call.state === STATES.REQUESTING_MIC) setChanges([]);
  }, [call.state]);

  const isActive = call.state === STATES.ACTIVE;
  const isError  = call.state === STATES.ERROR;
  const isStarting = call.state === STATES.REQUESTING_MIC || call.state === STATES.CONNECTING;

  const costLine = useMemo(() => {
    if (!isActive) return null;
    const text = formatCost(call.usage && call.usage.cost);
    if (text === '—') return null;
    return { text, provider: modelLabel(call.model) };
  }, [isActive, call.usage, call.model]);

  return (
    <div className="consultant-panel teach-voice-panel">
      <header className="tvp-header">
        <div>
          <h2>کلاس آموزش صوتی</h2>
          <p className="tvp-intro">
            با مشاور صوتی صحبت کنید تا به او دانش جدید بیاموزید.
            هر چیزی که بگویید (نام اپراتور، طرح، قیمت، شرایط، …) به‌صورت
            نتِ خودبسنده در پایگاه دانش ذخیره می‌شود و در گفتگوهای بعدی
            با مشتری مورد استفاده قرار می‌گیرد. پیش از پاک‌کردن یک نت،
            مشاور تأییدِ کلامیِ شما را می‌گیرد.
          </p>
        </div>
        {call.fellBack && (
          <div className="tvp-toast">ارائه‌دهندهٔ پشتیبان فعال شد</div>
        )}
      </header>

      <div className="tvp-controls">
        {call.state === STATES.IDLE && (
          <button
            type="button"
            className="tvp-start-btn"
            onClick={call.startCall}
          >
            شروع کلاس آموزش صوتی
          </button>
        )}
        {isStarting && (
          <button type="button" className="tvp-start-btn" disabled>
            در حال اتصال…
          </button>
        )}
        {(isActive || call.state === STATES.ENDING) && (
          <>
            <div className="tvp-status-row">
              <span className="tvp-status-dot is-active" />
              <span>کلاس فعال است - صحبت کنید</span>
              <span className="tvp-timer">
                {String(Math.floor(call.elapsedSec / 60)).padStart(2,'0')}:
                {String(call.elapsedSec % 60).padStart(2,'0')}
              </span>
            </div>
            <div className="tvp-actions">
              <button
                type="button"
                className={`tvp-mute-btn${call.isMuted ? ' on' : ''}`}
                onClick={call.toggleMute}
                disabled={!isActive}
              >
                {call.isMuted ? 'باز کردن میکروفون' : 'بستن میکروفون'}
              </button>
              <button
                type="button"
                className="tvp-end-btn"
                onClick={call.endCall}
              >
                پایان کلاس
              </button>
            </div>
            {costLine && (
              <div className="tvp-cost" title="هزینهٔ تخمینیِ کلاس بر اساس قیمت‌نامهٔ عمومیِ مدل">
                <span>هزینهٔ تخمینی</span>
                <strong>{costLine.text}</strong>
                <span className="tvp-cost-provider">{costLine.provider}</span>
              </div>
            )}
          </>
        )}
        {isError && (
          <div className="tvp-error" role="alert">
            {call.error || 'خطایی پیش آمد؛ لطفاً دوباره تلاش کنید.'}
          </div>
        )}
      </div>

      <section className="tvp-board" aria-label="تابلوی کلاس">
        <h3>تابلوی کلاس</h3>
        {changes.length === 0 ? (
          <p className="tvp-board-empty">
            {isActive
              ? 'هنوز چیزی به مشاور آموزش داده نشده. شروع به صحبت کنید.'
              : 'تغییراتِ هر کلاس پس از شروع اینجا ظاهر می‌شود.'}
          </p>
        ) : (
          <ul className="tvp-board-list">
            {changes.map((c) => (
              <li key={c.id} className={`tvp-board-row tvp-action-${c.action}`}>
                <span className="tvp-board-action">{ACTION_LABEL[c.action] || c.action}</span>
                <span className="tvp-board-preview">
                  {c.content_preview || (c.args && (c.args.content || c.args.query || c.args.note_id)) || '—'}
                </span>
                {c.in_flight && <span className="tvp-board-pending">در حال انجام…</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
